import React, { useRef, useState } from 'react';
import { useGame } from '../../contexts/GameContext';
import { CharacterStatsContent } from '../../Systems/CharacterStatsUI';
import { classColor, formatCallingName } from '../../theme';
import { CHARACTER_THEME_STYLE, type CharacterTab } from './characterPresentation';
import { EquipmentContent } from './EquipmentContent';

const CHARACTER_TABS = ['equipment', 'stats'] as const;

export const CharacterContent: React.FC = () => {
  const { player } = useGame();
  const [activeTab, setActiveTab] = useState<CharacterTab>('equipment');
  const tabRefs = useRef<Record<CharacterTab, HTMLButtonElement | null>>({
    equipment: null,
    stats: null,
  });

  const selectTab = (tab: CharacterTab, moveFocus = false) => {
    setActiveTab(tab);
    if (moveFocus) tabRefs.current[tab]?.focus();
  };

  const handleTabKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    const currentIndex = CHARACTER_TABS.indexOf(activeTab);
    let nextIndex: number | null = null;
    if (event.key === 'ArrowRight') nextIndex = (currentIndex + 1) % CHARACTER_TABS.length;
    if (event.key === 'ArrowLeft') nextIndex = (currentIndex - 1 + CHARACTER_TABS.length) % CHARACTER_TABS.length;
    if (event.key === 'Home') nextIndex = 0;
    if (event.key === 'End') nextIndex = CHARACTER_TABS.length - 1;
    if (nextIndex == null) return;
    event.preventDefault();
    selectTab(CHARACTER_TABS[nextIndex], true);
  };

  const identityReady = player != null
    && typeof player.name === 'string'
    && typeof player.class === 'string'
    && Number.isFinite(player.level);
  const identityStyle = identityReady
    ? ({ '--character-class-color': classColor(player.class) } as React.CSSProperties)
    : undefined;

  return (
    <div
      className="character-sheet"
      data-testid="character-window"
      style={CHARACTER_THEME_STYLE}
    >
      <header className="character-identity" style={identityStyle}>
        {identityReady ? (
          <>
            <div className="character-identity__mark" aria-hidden="true">
              {player.name.slice(0, 1).toUpperCase()}
            </div>
            <div className="character-identity__copy">
              <p className="character-eyebrow">Adventurer dossier</p>
              <h1>{player.name}</h1>
            </div>
            <dl className="character-identity__facts">
              <div>
                <dt>Class</dt>
                <dd>{formatCallingName(player.class)}</dd>
              </div>
              <div>
                <dt>Level</dt>
                <dd>{player.level}</dd>
              </div>
            </dl>
          </>
        ) : (
          <div className="character-identity__unavailable" role="status">
            <h1>Character unavailable</h1>
            <p>Waiting for your character to load.</p>
          </div>
        )}
      </header>

      <div role="tablist" aria-label="Character sections" className="character-tabs">
        {CHARACTER_TABS.map(tab => {
          const selected = activeTab === tab;
          const label = tab === 'equipment' ? 'Equipment' : 'Stats';
          return (
            <button
              key={tab}
              ref={element => { tabRefs.current[tab] = element; }}
              id={`character-tab-${tab}`}
              type="button"
              role="tab"
              aria-selected={selected}
              aria-controls={`character-panel-${tab}`}
              tabIndex={selected ? 0 : -1}
              onClick={() => selectTab(tab)}
              onKeyDown={handleTabKeyDown}
            >
              {label}
            </button>
          );
        })}
      </div>

      <div
        id="character-panel-equipment"
        role="tabpanel"
        aria-labelledby="character-tab-equipment"
        className="character-tabpanel"
        hidden={activeTab !== 'equipment'}
      >
        <EquipmentContent />
      </div>
      <div
        id="character-panel-stats"
        role="tabpanel"
        aria-labelledby="character-tab-stats"
        className="character-tabpanel"
        hidden={activeTab !== 'stats'}
      >
        <CharacterStatsContent />
      </div>
    </div>
  );
};
